import type { KeyboardEvent, SyntheticEvent } from "react";

const stopPositionTracking = new WeakMap<HTMLDetailsElement, () => void>();

export function closePopupMenu(menuItem: HTMLElement): void {
  const menu = menuItem.closest("details");
  if (!(menu instanceof HTMLDetailsElement)) return;
  menu.open = false;
  menu.querySelector<HTMLElement>("summary")?.focus();
}

export function focusOpenedPopupMenu(
  event: SyntheticEvent<HTMLDetailsElement>,
): void {
  const details = event.currentTarget;
  const summary = details.querySelector("summary");
  if (summary) summary.setAttribute("aria-expanded", String(details.open));
  if (!details.open) {
    stopTrackingPopupMenuPosition(details);
    delete details.dataset.menuFocus;
    const panel = details.querySelector<HTMLElement>("[role=menu]");
    if (panel) delete panel.dataset.positioned;
    return;
  }
  positionPopupMenu(details);
  trackPopupMenuPosition(details);
  const items = getMenuItems(details);
  const index = details.dataset.menuFocus === "last" ? items.length - 1 : 0;
  delete details.dataset.menuFocus;
  moveMenuFocus(items, index);
}

function trackPopupMenuPosition(details: HTMLDetailsElement): void {
  stopTrackingPopupMenuPosition(details);
  const reposition = () => {
    if (details.open) positionPopupMenu(details);
  };
  window.addEventListener("resize", reposition);
  window.addEventListener("orientationchange", reposition);
  window.addEventListener("scroll", reposition, true);
  stopPositionTracking.set(details, () => {
    window.removeEventListener("resize", reposition);
    window.removeEventListener("orientationchange", reposition);
    window.removeEventListener("scroll", reposition, true);
  });
}

function stopTrackingPopupMenuPosition(details: HTMLDetailsElement): void {
  stopPositionTracking.get(details)?.();
  stopPositionTracking.delete(details);
}

export function cleanupPopupMenu(details: HTMLDetailsElement): void {
  stopTrackingPopupMenuPosition(details);
}

export function handlePopupMenuKeyDown(
  event: KeyboardEvent<HTMLDivElement>,
): void {
  const details = event.currentTarget.closest("details");
  if (!(details instanceof HTMLDetailsElement)) return;
  if (closeMenuForKey(event, details)) return;
  const items = getMenuItems(event.currentTarget);
  if (items.length === 0) return;
  const current = items.indexOf(event.target as HTMLButtonElement);
  const nextIndex = getNextMenuItemIndex(event.key, current, items.length);
  if (nextIndex === undefined) return;
  event.preventDefault();
  moveMenuFocus(items, nextIndex);
}

export function handlePopupMenuTriggerKeyDown(
  event: KeyboardEvent<HTMLElement>,
): void {
  if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
  const details = event.currentTarget.closest("details");
  if (!(details instanceof HTMLDetailsElement)) return;
  event.preventDefault();
  const focusPosition = event.key === "ArrowUp" ? "last" : "first";
  if (details.open) {
    const items = getMenuItems(details);
    moveMenuFocus(items, focusPosition === "last" ? items.length - 1 : 0);
    return;
  }
  details.dataset.menuFocus = focusPosition;
  details.open = true;
}

export function handlePopupMenuItemFocus(
  event: SyntheticEvent<HTMLDivElement>,
): void {
  if (!(event.target instanceof HTMLButtonElement)) return;
  const items = getMenuItems(event.currentTarget);
  const index = items.indexOf(event.target);
  if (index >= 0) setMenuTabStop(items, index);
}

function closeMenuForKey(
  event: KeyboardEvent<HTMLDivElement>,
  details: HTMLDetailsElement,
): boolean {
  if (event.key === "Escape") {
    event.preventDefault();
    details.open = false;
    details.querySelector<HTMLElement>("summary")?.focus();
    return true;
  }
  if (event.key !== "Tab") return false;
  details.open = false;
  return true;
}

function getNextMenuItemIndex(
  key: string,
  current: number,
  itemCount: number,
): number | undefined {
  switch (key) {
    case "Home":
      return 0;
    case "End":
      return itemCount - 1;
    case "ArrowDown":
      return current < 0 ? 0 : (current + 1) % itemCount;
    case "ArrowUp":
      return current <= 0 ? itemCount - 1 : current - 1;
    default:
      return undefined;
  }
}

function moveMenuFocus(items: HTMLButtonElement[], index: number): void {
  if (index < 0 || index >= items.length) return;
  setMenuTabStop(items, index);
  const item = items[index];
  item.focus();
}

function setMenuTabStop(items: HTMLButtonElement[], index: number): void {
  items.forEach((item, itemIndex) => {
    item.tabIndex = itemIndex === index ? 0 : -1;
  });
}

function getMenuItems(root: ParentNode): HTMLButtonElement[] {
  return Array.from(
    root.querySelectorAll<HTMLButtonElement>(
      'button[role="menuitem"]:not(:disabled)',
    ),
  );
}

function positionPopupMenu(details: HTMLDetailsElement): void {
  const trigger = details.querySelector<HTMLElement>("summary");
  const panel = details.querySelector<HTMLElement>("[role=menu]");
  if (!trigger || !panel) return;
  const margin = 8;
  const gap = 6;
  panel.style.removeProperty("width");
  const natural = panel.getBoundingClientRect();
  if (natural.width <= 0 || natural.height <= 0) {
    panel.dataset.positioned = "true";
    return;
  }
  const width = Math.min(natural.width, window.innerWidth - margin * 2);
  panel.style.width = `${String(width)}px`;
  const height = Math.min(
    panel.getBoundingClientRect().height,
    window.innerHeight - margin * 2,
  );
  const anchor = trigger.getBoundingClientRect();
  const left = Math.min(
    Math.max(anchor.left, margin),
    window.innerWidth - margin - width,
  );
  const below = anchor.bottom + gap;
  const top =
    below + height <= window.innerHeight - margin
      ? below
      : Math.max(margin, anchor.top - gap - height);
  panel.style.left = `${String(left)}px`;
  panel.style.top = `${String(top)}px`;
  panel.dataset.positioned = "true";
}
